import { Box, Typography } from '@mui/material';
import { memo, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Cell, Pie, PieChart, PieSectorDataItem, ResponsiveContainer, Sector, Tooltip } from 'recharts';
import dashboardApi from '~/apis/dashboard';
import RenderCustomLegend from '~/components/pie-chart/RenderCustomLegend';
import { useResponsiveValue } from '~/hooks/useResponsiveValue';
import { handleApiError } from '~/utils/helper';
import { IOverviewResult } from '~/utils/interface/dashboard';
import { IUser } from '~/utils/interface/user';

type DeviceSummaryProps = {
  currentUser?: IUser;
};

const renderCustomLabel = ({ cx, cy, midAngle, outerRadius, value, label, fill, responsiveLabel }: any) => {
  const RADIAN = Math.PI / 180;
  const sin = Math.sin(-RADIAN * midAngle);
  const cos = Math.cos(-RADIAN * midAngle);

  const sx = cx + outerRadius * cos;
  const sy = cy + (outerRadius + 5) * sin;
  const mx = cx + (outerRadius + 10) * cos;
  const my = cy + (outerRadius + 20) * sin;
  const ex = mx + (cos >= 0 ? 1 : -1) * (responsiveLabel?.ex ?? 60);
  const ey = my;
  const textAnchor = cos >= 0 ? 'start' : 'end';

  const textPosition = responsiveLabel?.textPosition ?? 0;
  return value ? (
    <g>
      <path d={`M${sx},${sy}L${mx},${my}L${ex},${ey}`} stroke={fill} fill='none' />
      <text
        x={(cos >= 0 ? mx + textPosition - 25 : mx - textPosition + 25) + (cos >= 0 ? 1 : -1) * 12}
        y={ey - 5}
        textAnchor={textAnchor}
        fill='#161A1F'
        fontSize={responsiveLabel?.fontSize}
      >
        {label}
      </text>
      <text
        x={(cos >= 0 ? mx + textPosition : mx - textPosition) + (cos >= 0 ? 1 : -1) * 12}
        y={ey}
        dy={18}
        textAnchor={textAnchor}
        fill={fill}
        fontWeight={600}
        fontSize={responsiveLabel?.fontSize}
      >
        {value}
      </text>
    </g>
  ) : (
    <></>
  );
};

const DevicesSummary = (props: DeviceSummaryProps) => {
  const { t } = useTranslation('admin/home');

  const [overviewData, setOverviewData] = useState<IOverviewResult | null>(null);

  const [data, setData] = useState<
    Array<{ label: string; name: string; value: number; color: string; percent: number; isEmpty?: boolean }>
  >([]);

  useEffect(() => {
    if (!props.currentUser?.username) return;

    let isCancelled = false;
    let timeoutId: ReturnType<typeof setTimeout>;

    const poll = async () => {
      try {
        const response = await dashboardApi.getOverviewData({ username: props.currentUser?.username });
        if (!isCancelled) {
          setOverviewData((prev) => {
            const next = response.data.result;
            if (JSON.stringify(prev) === JSON.stringify(next)) {
              return prev;
            }
            return next;
          });
        }
      } catch (error) {
        if (!isCancelled) handleApiError(error);
      } finally {
        if (!isCancelled) {
          timeoutId = setTimeout(poll, 10000); // 10s
        }
      }
    };

    poll();

    return () => {
      isCancelled = true;
      clearTimeout(timeoutId);
    };
  }, [props.currentUser?.username]);

  const responsiveFontSize = useResponsiveValue({
    lg: '12px',
    xs: '10px',
    xl: '14px',
  });

  const responsiveLabel = useResponsiveValue({
    lg: {
      fontSize: '12px',
      textPosition: 15,
      ex: 50,
    },
    xl: {
      fontSize: '12px',
      textPosition: 26,
      ex: 80,
    },
  });

  useEffect(() => {
    if (overviewData?.device) {
      const total: number = overviewData?.device.total || 0;

      const chartData = [
        {
          label: t('device.unassigned'),
          name: t('device.numberOfUnassigned'),
          value: overviewData?.device.unassigned,
          color: '#C62828',
          percent: overviewData?.device.unassigned / total,
        },
        {
          label: t('device.assigned'),
          name: t('device.numberOfAssigned'),
          value: overviewData?.device.assigned,
          color: '#17A62B',
          percent: overviewData?.device.assigned / total,
        },
      ].filter((item) => item.value > 0);

      if (chartData.length === 0) {
        setData([
          {
            value: 1,
            color: '#E5E7EB',
            label: '',
            name: '',
            percent: 0,
            isEmpty: true,
          },
        ]);
        return;
      }

      setData(chartData);
    }
  }, [t, overviewData?.device]);

  const CustomTooltip = ({ active, payload }: any) => {
    if (!active || !payload?.length) return null;

    const data = payload[0];
    if (data.payload.isEmpty) return null;

    return (
      <Box
        className='rounded-md bg-white p-2 shadow-lg flex justify-between items-center'
        sx={{
          border: '1px solid ' + data.color,
        }}
      >
        <Box className='mr-1'>{data.payload.label}:</Box>
        <Box className='tooltip-value'>
          {data.value} ({(data.payload.percent * 100).toFixed(0)}%)
        </Box>
      </Box>
    );
  };

  const renderActiveShape = (props: PieSectorDataItem) => {
    const RADIAN = Math.PI / 180;

    const { cx = 0, cy = 0, midAngle = 0, innerRadius = 0, outerRadius = 0, startAngle, endAngle, fill } = props;

    const sin = Math.sin(-RADIAN * midAngle);
    const cos = Math.cos(-RADIAN * midAngle);

    const sx = cx + (outerRadius + 5) * cos;
    const sy = cy + (outerRadius + 5) * sin;

    const mx = cx + (outerRadius + 20) * cos;
    const my = cy + (outerRadius + 20) * sin;

    const ex = mx + (cos >= 0 ? 1 : -1) * 50;
    const ey = my;

    return (
      <g>
        <Sector
          cx={cx}
          cy={cy}
          innerRadius={innerRadius}
          outerRadius={outerRadius + 8}
          startAngle={startAngle}
          endAngle={endAngle}
          fill={fill}
        />

        {/* line */}
        <path d={`M${sx},${sy}L${mx},${my}L${ex},${ey}`} stroke={fill} fill='none' />
      </g>
    );
  };

  return (
    <Box className='rounded-xl bg-white flex flex-col'>
      <Typography className='bg-[#123B6D] text-white font-semibold text-xl rounded-t-xl p-3'>
        {t('device.title')}
      </Typography>
      <Box
        className='w-full'
        style={{
          width: '100%',
          height: 320,
        }}
      >
        <ResponsiveContainer>
          <PieChart>
            <Pie
              data={data}
              dataKey='value'
              nameKey='name'
              cx='50%'
              cy='45%'
              innerRadius='20%'
              outerRadius='60%'
              startAngle={90}
              endAngle={-270}
              fill='#82ca9d'
              isAnimationActive={false}
              labelLine={false}
              className='hover:cursor-pointer'
              activeShape={data?.[0]?.isEmpty ? undefined : renderActiveShape}
              label={(prev) => (prev?.payload?.isEmpty ? null : renderCustomLabel({ ...prev, responsiveLabel }))}
            >
              {data.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} />
              ))}
              <text
                x='50%'
                y={data?.[0]?.isEmpty ? '46%' : '43%'}
                textAnchor='middle'
                dominantBaseline='middle'
                fontSize={18}
                fontWeight='500'
                fill='#404854'
              >
                {overviewData?.device?.total ?? 0}
              </text>
            </Pie>
            <Tooltip content={<CustomTooltip />} />
            {!data?.[0]?.isEmpty && <RenderCustomLegend fontSize={responsiveFontSize ?? '12px'} />}
          </PieChart>
        </ResponsiveContainer>
      </Box>
    </Box>
  );
};

export default memo(DevicesSummary);
